import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import AmbientBackground from '../common/AmbientBackground';
import Button from '../common/Button';

const VirtualDeckPage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <AmbientBackground mood="sun" backgroundImage="/bg-still-glow.png">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="max-w-4xl mx-auto"
      >
        <div className="bg-parchment/60 dark:bg-shadow/60 border border-mist dark:border-dusk rounded-xl p-6 mb-6">
          <h1 className="text-3xl md:text-4xl font-display font-semibold text-ink dark:text-parchment mb-4">
            The Virtual Deck Experience
          </h1>
          <div className="space-y-4">
            <p className="text-lg text-ink dark:text-parchment/60">
              You can still use this app even if you don't have a physical deck, though using one lets you connects you and your cards energetically. Still, there are two great ways to explore tarot virtually on this app.
            </p>
            <p className="text-lg text-ink dark:text-parchment/60">
              Need to know the basics of reading Tarot? Visit the <Link to="/guide/101" className="text-sun dark:text-ember hover:text-sun dark:hover:text-ember">Tarot 101</Link> page.
            </p>
          </div>
        </div>

        <div className="bg-parchment/60 dark:bg-shadow/60 border border-mist dark:border-dusk rounded-xl p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-parchment/60 dark:bg-shadow/60 rounded-xl overflow-hidden border border-mist dark:border-dusk">
              <div className="p-6 h-full flex flex-col">
                <h2 className="text-2xl font-display font-semibold text-ink dark:text-parchment mb-3">
                  See a Full Tarot Deck
                </h2>
                <p className="text-ink dark:text-parchment/60 mb-6 flex-grow">
                  See all 78 tarot cards virtually. This will help you become familiar with the images in readings.
                </p>
                <Button
                  variant="primary"
                  onClick={() => navigate('/deck')}
                  fullWidth
                >
                  View Deck
                </Button>
              </div>
            </div>

            <div className="bg-parchment/60 dark:bg-shadow/60 rounded-xl overflow-hidden border border-mist dark:border-dusk">
              <div className="p-6 h-full flex flex-col">
                <h2 className="text-2xl font-display font-semibold text-ink dark:text-parchment mb-3">
                  Get a Virtual Reading
                </h2>
                <p className="text-ink dark:text-parchment/60 mb-6 flex-grow">
                  Try a virtual tarot reading. Perfect for daily card draws or more complex tarot spread layouts.
                </p>
                <Button
                  variant="secondary"
                  onClick={() => navigate('/virtual')}
                  fullWidth
                >
                  Start Virtual Reading
                </Button>
              </div>
            </div>
          </div>

          <div className="mt-8 bg-parchment/60 dark:bg-shadow/60 rounded-lg p-6 border border-mist dark:border-dusk">
            <h3 className="text-xl font-display font-semibold text-ink dark:text-parchment mb-4">
              ✨ About Virtual Readings
            </h3>
            <div className="space-y-4 text-ink dark:text-parchment/60">
              <p>
                Virtual readings offer a convenient way to practice tarot and receive guidance, especially when:
              </p>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <span className="text-sun dark:text-ember font-bold mr-2">✧</span>
                  <span>You're learning tarot and want to familiarize yourself with the cards</span>
                </li>
                <li className="flex items-start">
                  <span className="text-sun dark:text-ember font-bold mr-2">✧</span>
                  <span>You don't have your physical deck with you</span>
                </li>
                <li className="flex items-start">
                  <span className="text-sun dark:text-ember font-bold mr-2">✧</span>
                  <span>You want to try different spreads and layouts</span>
                </li>
                <li className="flex items-start">
                  <span className="text-sun dark:text-ember font-bold mr-2">✧</span>
                  <span>You'd like a quick daily card draw for guidance</span>
                </li>
              </ul>
              <p className="mt-4">
                While virtual readings are convenient, we encourage you to develop a relationship with physical cards when possible. Each method offers its own unique benefits to your practice.
              </p>
            </div>
          </div>

          <div className="mt-8 text-center space-x-4">
            <Link
              to="/center"
              className="text-sun dark:text-ember hover:text-sun dark:hover:text-ember"
            >
              Tarot Center
            </Link>
            <span className="text-mist">•</span>
            <Link
              to="/guide/101"
              className="text-sun dark:text-ember hover:text-sun dark:hover:text-ember"
            >
              Tarot 101
            </Link>
            <span className="text-mist">•</span>
            <Link
              to="/faq"
              className="text-sun dark:text-ember hover:text-sun dark:hover:text-ember"
            >
              About This App
            </Link>
          </div>
        </div>
      </motion.div>
    </AmbientBackground>
  );
};

export default VirtualDeckPage;
